import React from "react";
import { Box, Typography } from "@mui/material";
import { ResourceCentre } from "../../LabprintComponents/LabPrintLetterhead";
import LabPrintFooter from "../../LabprintComponents/LabPrintFooter";
import LabPrintClientInfo from "../../LabprintComponents/LabPrintClientInfo";
import LabPrintLabInfo from "../../LabprintComponents/LabPrintLabInfo";
import AuthorizedSPInfo, { AuthorizedSp } from "../../LabprintComponents/AuthorizedSPInfo";
import LabPrintCoreData from "./DefaultTemplateCore";
import {
  applySpaciousMargins,
  getSectionStyle,
  groupLabTestByCategory,
  scaleFont,
  showDynamicSignature
} from "../../LabPrintFunctions";

import { LabObject, LabPrintSettings } from "../../../../../interfaces/LabInterfaces";
import { CustomEl } from "../../../LabEntry/LabTestSubRow";
import InfoField from "../../LabprintComponents/LabPrintInfofield";
import { getFooterHeight } from "../../labPrintHelpers";
import { getLabHeader } from "../A5/A5Template";
import CustomHeader from "../../CustomHeader";
import { t } from "../../../../../components/translate";
import { spFullNameSelector } from "../../../../../reducers/serviceProvider";
import { LabAttachmentPrint } from "../../LabAttachmentPrint";
import { LabTestStatusTypes } from "../../../LabList";

interface ExtraProps {
  unfixFooter?: boolean;
}

interface DefaultTemplateProps {
  resourceCentre: ResourceCentre;
  labData: LabObject;
  settings: LabPrintSettings;
  otherProps?: ExtraProps;
  showAdditionalLabData: boolean;
  showDobOnLabPrint: boolean;
  showLabOrderedBy: boolean;
  showAttachmentOnlyInPrint: boolean;
  showBarcodeValue?: boolean;
}

const DefaultTemplate: React.FC<DefaultTemplateProps> = ({
  settings,
  resourceCentre,
  labData,
  otherProps = {},
  showAdditionalLabData,
  showDobOnLabPrint,
  showLabOrderedBy,
  showAttachmentOnlyInPrint = false,
  showBarcodeValue = false
}) => {
  const authorizedLabSp1: AuthorizedSp = labData.results?.approvedBy?.authorizedSP1 || null;
  const authorizedLabSp2: AuthorizedSp = labData.results?.approvedBy?.authorizedSP2 || null;
  const authorizedLabSp3: AuthorizedSp = labData.results?.approvedBy?.authorizedSP3 || null;
  const authorizedLabSp4: AuthorizedSp = labData.results?.approvedBy?.authorizedSP4 || null;

  const {
    showLetterhead,
    showFooter,
    headerColor,
    dateSetting,
    showPrintedOnTime,
    showReportOnTime,
    showSampleTakenTime,
    showSampleTakenDate,
    showReportOnDate,
    showPrintedOnDate,
    spaciousMargins,
    centralizedHeader,
    clinicNameFontScale,
    showLabRemarks,
    labRemarks,
    showMethods,
    showPhoto,
    showCustomerIdentifier,
    customTitle,
    showRanges,
    showUnit,
    showReading,
    hideFooterLogoCompletely,
    showRangeFlag,
    useCustomLabTestNumber,
    showQrWhenFooterIsHidden,
    showSampleCollectedBy,
    customHeaderImage
  } = settings;

  const profileImage = labData.results?.client?.profileImage || null;
  const printTitle =
    labData?.status === LabTestStatusTypes.ORDERED
      ? "Lab Tests Requests"
      : customTitle ?? "Lab Test Result";

  return (
    <div>
      <Box component="table" width="100%">
        <tfoot>
          <tr>
            <td>
              {/* footer offset */}
              <div
                style={{
                  height: getFooterHeight({
                    showFooter,
                    showQrWhenFooterIsHidden
                  })
                }}
              />
            </td>
          </tr>
        </tfoot>

        <thead>
          {!showLetterhead && (
            <tr>
              <td>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <div style={{ height: "3.5cm" }} />
              </td>
            </tr>
          )}
          {showLetterhead && (
            <tr>
              <td>
                <Box style={{ ...getSectionStyle(headerColor).headerStyle }}>
                  {showLetterhead &&
                    (customHeaderImage ? (
                      <CustomHeader imageUrl={customHeaderImage} />
                    ) : (
                      getLabHeader(centralizedHeader, resourceCentre, clinicNameFontScale)
                    ))}
                </Box>
              </td>
            </tr>
          )}
        </thead>

        <tbody>
          <tr>
            <td>
              <Box style={{ ...getSectionStyle(headerColor).bodyStyle }}>
                <Box
                  display="flex"
                  marginBottom="0.5cm"
                  justifyContent="space-between"
                  border="1px solid #bbb"
                  p="0.03cm 0.3cm"
                  gap={2}
                >
                  <Box component="div" flexGrow={1}>
                    <LabPrintClientInfo
                      showNameAndAgeInDiffRow
                      ipd={
                        labData?.ipd
                          ? {
                              ...labData.ipd,
                              bedNumber: labData.results?.bed?.name || ""
                            }
                          : {}
                      }
                      client={labData.client}
                      customerNumber={labData.customer?.customerNumber}
                      customerIdentifier={
                        (showCustomerIdentifier && labData.customer?.externalIdentifier) || ""
                      }
                      showDobOnLabPrint={showDobOnLabPrint}
                    />
                    {labData.labIdentifier && (
                      <InfoField
                        label="Lab Id"
                        data={labData.labIdentifier}
                        labelStyle={{ width: "2.9cm", fontSize: scaleFont("0.28cm", 1.2) }}
                        valueStyle={{ fontSize: scaleFont("0.28cm", 1.2) }}
                      />
                    )}
                    {showLabOrderedBy && (
                      <InfoField
                        label="Ordered By"
                        data={
                          labData?.serviceProvider
                            ? spFullNameSelector(labData.serviceProvider)
                            : t("labTest.orderBy.self")
                        }
                        labelStyle={{ width: "2.9cm", fontSize: scaleFont("0.28cm", 1.2) }}
                        valueStyle={{ fontSize: scaleFont("0.28cm", 1.2) }}
                      />
                    )}
                    {labData.referrers && (
                      <InfoField
                        label="Referred By"
                        data={labData.referrers.toUpperCase()}
                        labelStyle={{ width: "2.9cm", fontSize: scaleFont("0.28cm", 1.2) }}
                        valueStyle={{ fontSize: scaleFont("0.28cm", 1.2) }}
                      />
                    )}
                    {labData.extraReferrer?.name && (
                      <InfoField
                        label="Extra Referrer"
                        data={labData.extraReferrer.name.toUpperCase()}
                        labelStyle={{ width: "2.9cm", fontSize: scaleFont("0.28cm", 1.2) }}
                        valueStyle={{ fontSize: scaleFont("0.28cm", 1.2) }}
                      />
                    )}
                  </Box>
                  <LabPrintLabInfo
                    dateSetting={dateSetting}
                    labTestId={
                      useCustomLabTestNumber ? labData.labTestNumber || labData.id : labData.id
                    }
                    showPrintedOnTime={showPrintedOnTime}
                    showReportOnTime={showReportOnTime}
                    showSampleTakenTime={showSampleTakenTime}
                    showSampleTakenDate={showSampleTakenDate}
                    showPrintedOnDate={showPrintedOnDate}
                    showReportOnDate={showReportOnDate}
                    sampleRemarks={
                      Array.isArray(labData.remarks) && labData.remarks.length > 0
                        ? labData.remarks[0].remark
                        : null
                    }
                    collectionDate={labData.collectionDate}
                    resultDate={labData.resultDate}
                    modifiedDate={labData.results.modifiedDate || null}
                    showBarcodeValue={showBarcodeValue}
                  />
                  {showPhoto && (
                    <Box width="2.5cm" display="flex" justifyContent="flex-end">
                      {profileImage && (
                        <img
                          src={profileImage}
                          style={{
                            height: "auto",
                            width: "2.5cm",
                            objectFit: "cover",
                            ...(profileImage && { minHeight: "4cm" })
                          }}
                          alt="Profile"
                        />
                      )}
                    </Box>
                  )}
                </Box>
                {spaciousMargins && applySpaciousMargins()}
                <Typography
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    fontSize: "0.5cm",
                    fontWeight: 600,
                    textTransform: "uppercase"
                  }}
                >
                  {printTitle}
                </Typography>
                <>
                  {spaciousMargins && applySpaciousMargins(2)}
                  {showAttachmentOnlyInPrint && (
                    <Box>
                      {Array.isArray(labData.labRecordAttachments) &&
                        labData.labRecordAttachments.map((attachment) => (
                          <LabAttachmentPrint key={attachment.id} attachment={attachment} />
                        ))}
                    </Box>
                  )}
                  {!showAttachmentOnlyInPrint && (
                    <Box>
                      <LabPrintCoreData
                        labData={groupLabTestByCategory(labData)}
                        showAdditionalLabData={showAdditionalLabData}
                        showMethods={showMethods}
                        showRanges={showRanges}
                        showUnit={showUnit}
                        showReading={showReading}
                        showRangeFlag={showRangeFlag}
                        showSampleCollectedBy={showSampleCollectedBy}
                      />
                    </Box>
                  )}
                  {spaciousMargins && applySpaciousMargins()}
                  {labData.results?.currentApprovedUser ? (
                    <Box
                      style={{ pageBreakInside: "avoid" }}
                      display="flex"
                      mt="0.1cm"
                      justifyContent="space-between"
                    >
                      <AuthorizedSPInfo sp={labData.results?.currentApprovedUser} />
                    </Box>
                  ) : (
                    <Box
                      style={{ pageBreakInside: "avoid" }}
                      display="flex"
                      gap="30px"
                      justifyContent="space-between"
                    >
                      {showDynamicSignature(authorizedLabSp1) && (
                        <Box>
                          <AuthorizedSPInfo sp={authorizedLabSp1} />
                        </Box>
                      )}
                      {showDynamicSignature(authorizedLabSp2, authorizedLabSp1) && (
                        <Box>
                          <AuthorizedSPInfo sp={authorizedLabSp2} />
                        </Box>
                      )}
                      {showDynamicSignature(
                        authorizedLabSp3,
                        authorizedLabSp1,
                        authorizedLabSp2
                      ) && (
                        <Box>
                          <AuthorizedSPInfo sp={authorizedLabSp3} />
                        </Box>
                      )}
                      {showDynamicSignature(
                        authorizedLabSp4,
                        authorizedLabSp1,
                        authorizedLabSp2,
                        authorizedLabSp3
                      ) && (
                        <Box>
                          <AuthorizedSPInfo sp={authorizedLabSp4} />
                        </Box>
                      )}
                    </Box>
                  )}
                  {showLabRemarks && (
                    <Typography style={{ marginTop: "16px" }}>
                      <CustomEl data={labRemarks} />
                    </Typography>
                  )}
                </>
              </Box>
            </td>
          </tr>
        </tbody>
      </Box>
      <LabPrintFooter
        uuid={labData.uuid}
        headerColor={headerColor}
        id={labData.id}
        unfixFooter={otherProps?.unfixFooter}
        showFooter={showFooter}
        hideFooterLogoCompletely={hideFooterLogoCompletely}
        showQRCode={showQrWhenFooterIsHidden}
      />
    </div>
  );
};

export default DefaultTemplate;
